import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Shield, AlertTriangle, Clock, Activity, RefreshCw, Save } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Card, Badge, Button, Spinner } from "@/components/kit";

/**
 * WA Safety dashboard card — daily-limit, min-delay, and failure-rate auto-pause
 * thresholds. Surfaces today's usage so the user knows where they stand.
 *
 * Mount-anywhere card. Used inside Devices page.
 */
export default function WaSafetyCard() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/wa/safety");
      setData(r.data);
      setForm(r.data.settings);
    } catch (e) { /* silent — UI shows loading */ }
  };
  useEffect(() => { load(); }, []);

  if (!data || !form) return <Card className="p-6"><Spinner className="w-6 h-6 mx-auto" /></Card>;

  const pct = Math.min(100, Math.round((data.sent_today / form.daily_limit) * 100));
  const barColor = pct >= 90 ? "bg-rose-500" : pct >= 70 ? "bg-amber-500" : "bg-emerald-500";

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/wa/safety", form);
      setData(r.data);
      setForm(r.data.settings);
      toast.success("Pengaturan WA Safety disimpan");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const reset = () => setForm(data.defaults);

  return (
    <Card className="p-6 space-y-5" data-testid="wa-safety-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 grid place-items-center">
            <Shield className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h3 className="font-display font-bold text-lg">WA Safety</h3>
            <p className="text-xs text-slate-500">Lindungi device dari restricted / banned WhatsApp.</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} title="Refresh">
          <RefreshCw className="w-3.5 h-3.5" />
        </Button>
      </div>

      {/* Today's usage bar */}
      <div data-testid="wa-safety-usage">
        <div className="flex items-end justify-between mb-1.5">
          <span className="text-xs text-slate-500">Pesan terkirim hari ini</span>
          <span className="text-sm font-bold text-slate-700">
            {data.sent_today.toLocaleString("id-ID")} / {form.daily_limit.toLocaleString("id-ID")}
            <span className="text-xs text-slate-400 ml-1">({pct}%)</span>
          </span>
        </div>
        <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
          <div className={`h-full ${barColor} transition-all duration-500`} style={{ width: `${pct}%` }} />
        </div>
        <p className="text-xs text-slate-400 mt-1.5">
          Sisa hari ini: <b className={data.remaining_today === 0 ? "text-rose-500" : "text-emerald-600"}>{data.remaining_today}</b> pesan
        </p>
      </div>

      {/* Settings */}
      <div className="grid sm:grid-cols-3 gap-4">
        <SettingNum
          label="Limit harian"
          help={`Disarankan ≤ ${data.tip_safe_daily_limit} agar device aman.`}
          value={form.daily_limit}
          onChange={(v) => setForm({ ...form, daily_limit: v })}
          min={10} max={1000} step={10}
          icon={<Activity className="w-3.5 h-3.5" />}
          unit="pesan"
        />
        <SettingNum
          label="Delay minimum"
          help={`Disarankan ≥ ${data.tip_safe_min_delay} detik antar pesan.`}
          value={form.min_delay}
          onChange={(v) => setForm({ ...form, min_delay: v })}
          min={5} max={300} step={1}
          icon={<Clock className="w-3.5 h-3.5" />}
          unit="detik"
        />
        <SettingNum
          label="Auto-pause jika gagal"
          help="Hentikan otomatis kalau % failed lewat ambang ini."
          value={form.autopause_failed_pct}
          onChange={(v) => setForm({ ...form, autopause_failed_pct: v })}
          min={20} max={90} step={5}
          icon={<AlertTriangle className="w-3.5 h-3.5" />}
          unit="%"
        />
      </div>

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
        <button onClick={reset} className="text-xs font-semibold text-slate-500 hover:text-slate-800">Reset default</button>
        <Button onClick={save} loading={saving} size="sm">
          <Save className="w-3.5 h-3.5" /> Simpan
        </Button>
      </div>

      {/* Best-practice tips */}
      <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-3 text-xs text-emerald-800 space-y-1" data-testid="wa-safety-tips">
        <p className="font-semibold flex items-center gap-1.5"><Shield className="w-3.5 h-3.5" /> Tips agar device tetap aman:</p>
        <ul className="list-disc pl-5 space-y-0.5">
          <li>Delay 20–90 detik. Hindari pengiriman berurutan tanpa jeda.</li>
          <li>Maks 100–200 pesan/hari/nomor untuk WA Personal/Business.</li>
          <li>Pakai nomor "burner" khusus blast, bukan WA pribadi.</li>
          <li>Pesan natural &mdash; hindari ALL CAPS, banyak emoji, link bit.ly.</li>
          <li>Pakai <b>Cek WA</b> dulu, kirim hanya ke nomor yang ready.</li>
        </ul>
      </div>
    </Card>
  );
}

function SettingNum({ label, help, value, onChange, min, max, step, icon, unit }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600 flex items-center gap-1.5">{icon} {label}</label>
      <div className="mt-1.5 flex items-stretch rounded-xl border border-slate-200 overflow-hidden">
        <input type="number" min={min} max={max} step={step}
          value={value} onChange={(e) => onChange(Number(e.target.value))}
          className="flex-1 px-3 py-2 text-sm outline-none" />
        <span className="px-3 grid place-items-center text-xs bg-slate-50 text-slate-500 border-l border-slate-200">{unit}</span>
      </div>
      <p className="text-[11px] text-slate-400 mt-1">{help}</p>
    </div>
  );
}
